/**
 * The browser tab's title, written by whichever page header is the most specific one on screen.
 *
 * <p>A sectioned screen has two headers mounted at once — the section's (`level` 1) and the screen's
 * (`level` 2) — and they render in no order this module can rely on: switching tabs remounts only
 * the screen, switching language re-renders both. So each header registers what it says, and the
 * title is recomputed from the registry every time: the deepest level wins, and among equals the
 * one registered last. When the last header goes, the title goes back to the product's name.
 */
interface Entry {
  level: number;
  text: string;
  order: number;
}

const entries = new Map<symbol, Entry>();
let counter = 0;
let productName = "";

function apply(): void {
  if (typeof document === "undefined") {
    return;
  }
  let best: Entry | undefined;
  for (const entry of entries.values()) {
    if (!entry.text) {
      continue;
    }
    if (!best || entry.level > best.level || (entry.level === best.level && entry.order > best.order)) {
      best = entry;
    }
  }
  if (best) {
    document.title = productName ? `${best.text} · ${productName}` : best.text;
  } else if (productName) {
    document.title = productName;
  }
}

/** Registers or updates what one header says. Returns nothing; {@link releaseDocumentTitle} undoes it. */
export function claimDocumentTitle(key: symbol, level: number, text: string, product: string): void {
  productName = product;
  const existing = entries.get(key);
  if (existing) {
    if (existing.text === text && existing.level === level) {
      return;
    }
    entries.set(key, { ...existing, level, text });
  } else {
    counter += 1;
    entries.set(key, { level, text, order: counter });
  }
  apply();
}

export function releaseDocumentTitle(key: symbol): void {
  if (entries.delete(key)) {
    apply();
  }
}

/** Visible text of a heading, one space between its parts — a number, a name and a badge are three. */
export function headingText(node: HTMLElement | null): string {
  if (!node) {
    return "";
  }
  // `innerText`, not `textContent`: the parts of a detail title are flex items, and `textContent`
  // glues them together ("PC-2026-0001Etiopia Guji").
  return (node.innerText || node.textContent || "").replace(/\s+/g, " ").trim();
}
